import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ETM_TaskSortColumn, ETM_TaskSortDirection } from '__generated__/graphql';

// Re-export the GraphQL types for use in components
export type TaskSortColumn = ETM_TaskSortColumn | null;
export type SortDirection = ETM_TaskSortDirection;

/**
 * Maps German display labels to backend column enum values.
 * Used for converting user-selected sort options to GraphQL query parameters.
 */
export const sortColumnMap: Record<string, ETM_TaskSortColumn> = {
  'Betreff': ETM_TaskSortColumn.subject,
  'Kategorie': ETM_TaskSortColumn.category,
  'Fälligkeit': ETM_TaskSortColumn.dueDate,
  'Zugewiesen an': ETM_TaskSortColumn.assignee,
  'Erstellt von': ETM_TaskSortColumn.createdBy,
  'Hotel': ETM_TaskSortColumn.hotel,
  'Firma': ETM_TaskSortColumn.publicCompany,
  'Ansprechperson': ETM_TaskSortColumn.publicClient,
  'Buchung': ETM_TaskSortColumn.booking,
};

/**
 * Reverse mapping from backend column enum to German display labels.
 * Used for displaying current sort column in UI.
 */
export const sortColumnLabels: Record<string, string> = {
  [ETM_TaskSortColumn.subject]: 'Betreff',
  [ETM_TaskSortColumn.category]: 'Kategorie',
  [ETM_TaskSortColumn.dueDate]: 'Fälligkeit',
  [ETM_TaskSortColumn.assignee]: 'Zugewiesen an',
  [ETM_TaskSortColumn.createdBy]: 'Erstellt von',
  [ETM_TaskSortColumn.hotel]: 'Hotel',
  [ETM_TaskSortColumn.publicCompany]: 'Firma',
  [ETM_TaskSortColumn.publicClient]: 'Ansprechperson',
  [ETM_TaskSortColumn.booking]: 'Buchung',
};

interface TaskSortState {
  sortColumn: TaskSortColumn;
  sortDirection: SortDirection;
}

const initialState: TaskSortState = {
  sortColumn: ETM_TaskSortColumn.dueDate,
  sortDirection: ETM_TaskSortDirection.asc,
};

export const taskSortSlice = createSlice({
  name: 'taskSort',
  initialState,
  reducers: {
    setSortColumn: (state, action: PayloadAction<{ column: TaskSortColumn; direction?: SortDirection }>) => {
      const { column, direction } = action.payload;
      state.sortColumn = column;
      if (direction) {
        state.sortDirection = direction;
      }
    },
    toggleSort: (state, action: PayloadAction<TaskSortColumn>) => {
      const column = action.payload;

      if (state.sortColumn === column) {
        // Same column - toggle direction
        state.sortDirection = state.sortDirection === ETM_TaskSortDirection.asc ? ETM_TaskSortDirection.desc : ETM_TaskSortDirection.asc;
      } else {
        // New column - set to ascending
        state.sortColumn = column;
        state.sortDirection = ETM_TaskSortDirection.asc;
      }
    },
    resetTaskSort: (state) => {
      state.sortColumn = ETM_TaskSortColumn.dueDate;
      state.sortDirection = ETM_TaskSortDirection.asc;
    },
  },
});

export const { setSortColumn, toggleSort, resetTaskSort } = taskSortSlice.actions;
export const taskSortReducer = taskSortSlice.reducer;

export default taskSortSlice;
